import React, { useMemo, useState, useEffect } from "react";

const clp = (n) =>
  typeof n === "number" ? `$${n.toLocaleString("es-CL")}` : "—";

export default function ServicioVarianteSelector({
  serviciosFlat = [],
  servicioId,
  modalidad,
  planSeleccionado,       // ← nuevo
  onServicioChange,
  onModalidadChange,
  limpiarCupon,
  setPlanSeleccionado,
  setTipoUsoPlan,
}) {
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(() => {
    if (!servicioId) return "";
    const found = serviciosFlat.find((s) => s.id === servicioId);
    return found?.categoria || "";
  });

  // Cuando SlotModal cambia servicioId después de cargar servicios o un plan,
  // la categoría visible también debe actualizarse.
  useEffect(() => {
    if (!servicioId) return;
    const servicio = serviciosFlat.find((s) => s.id === servicioId);
    if (servicio?.categoria) {
      setCategoriaSeleccionada((actual) =>
        actual === servicio.categoria ? actual : servicio.categoria
      );
    }
  }, [servicioId, serviciosFlat]);

  const categorias = useMemo(() => {
    const map = new Map();
    serviciosFlat.forEach((s) => {
      if (!map.has(s.categoria)) map.set(s.categoria, []);
      map.get(s.categoria).push(s);
    });
    return Array.from(map.entries());
  }, [serviciosFlat]);

  const variantesDeCategoria = useMemo(() => {
    if (!categoriaSeleccionada) return [];
    return serviciosFlat.filter((s) => s.categoria === categoriaSeleccionada);
  }, [categoriaSeleccionada, serviciosFlat]);

  const servicioActual = useMemo(
    () => serviciosFlat.find((s) => s.id === servicioId) || null,
    [servicioId, serviciosFlat]
  );

  // ── Modalidades permitidas ────────────────────────────────────
  const mp = servicioActual?.modalidadesPermitidas;

  // Si no existe el campo, fallback a precio_grupo/precio_privada como antes
  const permiteGrupal = mp ? mp.grupal === true : typeof servicioActual?.precio_grupo === "number";
  const permitePrivada = mp ? mp.privada === true : typeof servicioActual?.precio_privada === "number";

  const hayAlgunaModalidad = permiteGrupal || permitePrivada;

  // Mostrar selector de modalidad SOLO si:
  // - la variante tiene modalidades configuradas
  // - NO hay plan activo seleccionado (la modalidad ya fue definida en el plan)
  const mostrarModalidad = servicioId && hayAlgunaModalidad && !planSeleccionado;

  const precioGrupal = servicioActual?.precio_grupo ?? servicioActual?.precio ?? 0;
  const precioPrivada = servicioActual?.precio_privada ?? servicioActual?.precio ?? 0;

  const handleCategoriaChange = (e) => {
    const nuevaCategoria = e.target.value;
    setCategoriaSeleccionada(nuevaCategoria);
    onServicioChange("", 0);
    setPlanSeleccionado?.(null);
    setTipoUsoPlan?.("ninguno");
    limpiarCupon?.();

    const variantes = serviciosFlat.filter((s) => s.categoria === nuevaCategoria);
    if (variantes.length === 1) {
      const v = variantes[0];
      const precio = modalidad === "privada"
        ? (v.precio_privada ?? v.precio ?? 0)
        : (v.precio_grupo ?? v.precio ?? 0);
      onServicioChange(v.id, precio);
    }
  };

  const handleVarianteChange = (e) => {
    const id = e.target.value;
    const v = serviciosFlat.find((s) => s.id === id);
    if (!v) { onServicioChange("", 0); return; }
    const precio = modalidad === "privada"
      ? (v.precio_privada ?? v.precio ?? 0)
      : (v.precio_grupo ?? v.precio ?? 0);
    onServicioChange(id, precio);
    setPlanSeleccionado?.(null);
    setTipoUsoPlan?.("ninguno");
    limpiarCupon?.();
  };

  const handleModalidad = (mod) => {
    if (!servicioActual) return;
    const precio = mod === "privada"
      ? (servicioActual.precio_privada ?? servicioActual.precio ?? 0)
      : (servicioActual.precio_grupo ?? servicioActual.precio ?? 0);
    onModalidadChange(mod, precio);
    limpiarCupon?.();
  };

  // Auto-seleccionar modalidad si solo hay una disponible
    useEffect(() => {
    if (!servicioActual) return;
    const mp = servicioActual.modalidadesPermitidas;
    if (!mp) return;

    const permiteGrupal = mp.grupal === true;
    const permitePrivada = mp.privada === true;

    // Solo auto-seleccionar si hay exactamente una opción
    if (permiteGrupal && !permitePrivada && modalidad !== "grupal") {
        handleModalidad("grupal");
    } else if (permitePrivada && !permiteGrupal && modalidad !== "privada") {
        handleModalidad("privada");
    }
    }, [servicioActual?.id]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-sm text-zinc-600 mb-1">Servicio</span>
          <select
            value={categoriaSeleccionada}
            onChange={handleCategoriaChange}
            className="w-full border border-zinc-300 rounded-md px-3 py-2 bg-white text-sm"
          >
            <option value="">Seleccione servicio…</option>
            {categorias.map(([cat]) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-sm text-zinc-600 mb-1">Variante</span>
          <select
            value={servicioId}
            onChange={handleVarianteChange}
            disabled={!categoriaSeleccionada}
            className="w-full border border-zinc-300 rounded-md px-3 py-2 bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Selecciona variante…</option>
            {variantesDeCategoria.map((v) => (
              <option key={v.id} value={v.id}>{v.variante}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Modalidad: solo si está permitida y NO hay plan activo */}
      {mostrarModalidad && (
        <div>
          <span className="block text-sm text-zinc-600 mb-2">
            Modalidad de la sesión
          </span>
          <div className="flex gap-3 flex-wrap">
            {permiteGrupal && (
              <button
                type="button"
                onClick={() => handleModalidad("grupal")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition ${
                  modalidad === "grupal"
                    ? "border-[#185FA5] bg-[#E6F1FB] text-[#0C447C] font-medium"
                    : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400"
                }`}
              >
                <span>Grupal</span>
                <span className="font-medium">{clp(precioGrupal)}</span>
              </button>
            )}
            {permitePrivada && (
              <button
                type="button"
                onClick={() => handleModalidad("privada")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition ${
                  modalidad === "privada"
                    ? "border-[#185FA5] bg-[#E6F1FB] text-[#0C447C] font-medium"
                    : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400"
                }`}
              >
                <span>Privada</span>
                <span className="font-medium">{clp(precioPrivada)}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Si hay plan activo, mostrar la modalidad del plan como info */}
      {servicioId && hayAlgunaModalidad && planSeleccionado && (
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-50 border border-zinc-200 text-sm text-zinc-600">
          <span>Modalidad definida por el plan:</span>
          <span className="font-medium text-zinc-800 capitalize">
            {planSeleccionado.modalidad || "—"}
          </span>
        </div>
      )}

      {/* Monto resultante */}
      {servicioId && (
        <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3">
          <span className="text-sm text-zinc-500">Monto</span>
          <div className="flex items-center gap-2">
            {hayAlgunaModalidad && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">
                {modalidad === "privada" ? "Privada" : "Grupal"}
              </span>
            )}
            <span className="text-base font-medium text-zinc-800">
              {clp(modalidad === "privada" ? precioPrivada : precioGrupal)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}